import React, { useEffect, useState } from "react";

import { Button, Stack } from "@mui/material";
import { useTranslation } from "react-i18next";

import {
  deleteConvienceFee,
  getAllActiveProviders,
  getConvienceFee,
} from "../../../Api/Signin/Transaction.Api";
import useLoadingState from "../../../Utils/Hooks/useLodingState";
import useOverlayState from "../../../Utils/Hooks/useOverlayState";
import { SideDrawer } from "../../atoms/Drawer/SideDrawer";
import { HorizontalDivide } from "../../atoms/HorizontalDivide/HorizontalDivide";
import ConfirmDialog from "../ConfirmDialog/ConfirmDialog";
import { ConvenienceFeeForm } from "./Content/ConvenienceFeeForm";
import { ConvenienceFeeTable } from "./Content/ConvenienceFeeTable";

export const ConvenienceFeeDrawer = ({ close, OpenDrawer }) => {
  const { t } = useTranslation();
  const { LoadingState, startLoading, stopLoading } = useLoadingState();
  const { OverlayState, openOverlay, closeOverlay } = useOverlayState();
  const [fees, setFees] = useState([]);
  const [providers, setProviders] = useState([]);
  const [mode, setMode] = useState("list");
  const [selectedFee, setSelectedFee] = useState(null);

  const mapConvenienceFees = (response) => {
    if (response?.type !== 0) return [];

    return (response.data || []).map((item) => ({
      id: item.id,
      providerId: item.providerId || "",
      providerName: item.providerName?.trim() || "",
      name: item.name?.trim() || "",
      type: item.type?.trim() || "",
      percentage: item.percentage ?? null,
      amount: item.amount ?? null,
      isActive: item.isActive ?? true,
    }));
  };

  const mapProviders = (response) => {
    if (response?.type !== 0) return [];

    return (response.data || []).map((item) => ({
      id: item.id,
      providerName: item.providerName?.trim() || "",
    }));
  };

  const loadConvenienceFees = async () => {
    try {
      startLoading({ btnName: "loadFees" });
      const response = await getConvienceFee();
      setFees(mapConvenienceFees(response));
    } catch (error) {
      setFees([]);
    } finally {
      stopLoading({ btnName: "loadFees" });
    }
  };

  const loadProviders = async () => {
    try {
      const response = await getAllActiveProviders();
      setProviders(mapProviders(response));
    } catch (error) {
      setProviders([]);
    }
  };

  const loadInitialData = async () => {
    await Promise.all([loadConvenienceFees(), loadProviders()]);
  };

  useEffect(() => {
    setMode("list");
    setSelectedFee(null);
    loadInitialData();
  }, [OpenDrawer]);

  const handleAddClick = () => {
    setSelectedFee(null);
    setMode("add");
  };

  const handleEditClick = (row) => {
    setSelectedFee(row);
    setMode("edit");
  };

  const handleDeleteClick = (row) => {
    setSelectedFee(row);
    openOverlay({ OverlayName: "delete-fee" });
  };

  const handleDeleteConfirm = async () => {
    try {
      if (!selectedFee?.id) return;
      startLoading({ btnName: "deleteFee" });
      const response = await deleteConvienceFee(selectedFee.id);
      if (
        response?.code === 200 ||
        response?.status === "Success" ||
        response?.type === 0
      ) {
        await loadConvenienceFees();
        setSelectedFee(null);
        closeOverlay();
      }
    } catch (error) {
      console.error("Delete Convenience Fee failed:", error);
    } finally {
      stopLoading({ btnName: "deleteFee" });
    }
  };

  const handleFormSuccess = async () => {
    await loadConvenienceFees();
    setSelectedFee(null);
    setMode("list");
  };

  const handleFormCancel = () => {
    setSelectedFee(null);
    setMode("list");
  };

  return (
    <>
      <SideDrawer
        headingText={t("glossary:convinience-fee.title")}
        width={"600px"}
        openDrawer={OpenDrawer}
        close={close}
        anchorDrawer={"right"}
        isLoading={LoadingState.visible}
      >
        <Stack marginTop={"16px"} rowGap={"20px"}>
          {mode === "list" && (
            <>
              <Stack direction="row" justifyContent="flex-end">
                <Button variant="primaryBlue" onClick={handleAddClick}>
                  {t("glossary:convinience-fee.add-convinience-fee")}
                </Button>
              </Stack>

              <ConvenienceFeeTable
                rows={fees}
                onEdit={handleEditClick}
                onDelete={handleDeleteClick}
                t={t}
              />
            </>
          )}

          {(mode === "add" || mode === "edit") && (
            <ConvenienceFeeForm
              t={t}
              mode={mode}
              selectedFee={selectedFee}
              providers={providers}
              fees={fees}
              onCancel={handleFormCancel}
              onSuccess={handleFormSuccess}
            />
          )}
        </Stack>

        <HorizontalDivide
          width={"100%"}
          marginBottom={"20px"}
          marginTop={"20px"}
        />
      </SideDrawer>

      {OverlayState.OverlayName === "delete-fee" && (
        <ConfirmDialog
          open={OverlayState.visible}
          close={closeOverlay}
          title={t("glossary:convinience-fee.delete-convenience-fee-title")}
          description={t("common:alert-confirm.proceed-confirm")}
          onOkClick={handleDeleteConfirm}
          onOkTitle={t("common:alert-confirm.yes")}
          onCancelClick={closeOverlay}
          onCancelTitle={t("common:alert-confirm.no")}
        />
      )}
    </>
  );
};
